import { shouldPolyfill as shouldPolyfillDateTimeFormat } from "@formatjs/intl-datetimeformat/should-polyfill";
import { shouldPolyfill as shouldPolyfillCanonicalLocales } from "@formatjs/intl-getcanonicallocales/should-polyfill";
import { shouldPolyfill as shouldPolyfillIntl } from "@formatjs/intl-locale/should-polyfill";
import { shouldPolyfill as shouldPolyfillNumberFormat } from "@formatjs/intl-numberformat/should-polyfill";
import { shouldPolyfill as shouldPolyfillPluralRules } from "@formatjs/intl-pluralrules/should-polyfill";
import { shouldPolyfill as shouldPolyfillRelativeTimeFormat } from "@formatjs/intl-relativetimeformat/should-polyfill";

async function polyfillPluralRules(locale: string) {
  if (!shouldPolyfillPluralRules(locale)) {
    return;
  }

  await import(
    /* webpackChunkName: "intl-pluralrules_polyfill" */ "@formatjs/intl-pluralrules/polyfill"
  );

  // Plural rules do not differ between regions - take the part of locale before the hyphen
  switch (locale.split("-").shift()) {
    default:
      await import("@formatjs/intl-pluralrules/locale-data/en");
      break;
    case "fr":
      await import("@formatjs/intl-pluralrules/locale-data/fr");
      break;
    case "es":
      await import("@formatjs/intl-pluralrules/locale-data/es");
      break;
  }
}

async function polyfillCanonicalLocales() {
  if (shouldPolyfillCanonicalLocales()) {
    await import(
      /* webpackChunkName: "intl-getcanonicallocales_polyfill" */ "@formatjs/intl-getcanonicallocales/polyfill"
    );
  }
}

async function polyfillIntl() {
  if (shouldPolyfillIntl()) {
    await import("@formatjs/intl-locale/polyfill");
  }
}
async function polyfillNumberFormat(locale: string) {
  locale = locale.toLowerCase();

  if (!shouldPolyfillNumberFormat(locale)) {
    return;
  }

  await import("@formatjs/intl-numberformat/polyfill");
  switch (locale) {
    default:
      await import("@formatjs/intl-numberformat/locale-data/en");
      break;
    case "en-gb":
      await import("@formatjs/intl-numberformat/locale-data/en-GB");
      break;
    case "fr":
      await import("@formatjs/intl-numberformat/locale-data/fr");
      break;
    case "es":
      await import("@formatjs/intl-numberformat/locale-data/es");
      break;
    case "es-mx":
      await import("@formatjs/intl-numberformat/locale-data/es-MX");
      break;
  }
}

async function polyfillDateTimeFormat(locale: string) {
  locale = locale.toLowerCase();

  if (!shouldPolyfillDateTimeFormat(locale)) {
    return;
  }

  await import("@formatjs/intl-datetimeformat/polyfill");

  // Parallelize CLDR data loading
  const dataPolyfills = [import("@formatjs/intl-datetimeformat/add-all-tz")];

  switch (locale) {
    default:
      dataPolyfills.push(
        import("@formatjs/intl-datetimeformat/locale-data/en"),
      );
      break;
    case "en-gb":
      dataPolyfills.push(
        import("@formatjs/intl-datetimeformat/locale-data/en-GB"),
      );
      break;
    case "fr":
      dataPolyfills.push(
        import("@formatjs/intl-datetimeformat/locale-data/fr"),
      );
      break;
    case "es-mx":
      dataPolyfills.push(
        import("@formatjs/intl-datetimeformat/locale-data/es-MX"),
      );
      break;
    case "es":
      dataPolyfills.push(
        import("@formatjs/intl-datetimeformat/locale-data/es"),
      );
      break;
  }
  await Promise.all(dataPolyfills);
}

async function polyfillRelativeTimeFormat(locale: string) {
  if (!shouldPolyfillRelativeTimeFormat) {
    return;
  }

  locale = locale.toLowerCase();

  await import("@formatjs/intl-relativetimeformat/polyfill");
  switch (locale) {
    case "en-gb":
      await import("@formatjs/intl-relativetimeformat/locale-data/en-GB");
      break;
    case "fr":
      await import("@formatjs/intl-relativetimeformat/locale-data/fr");
      break;
    case "es-mx":
      await import("@formatjs/intl-relativetimeformat/locale-data/es-MX");
      break;
    case "es":
      await import("@formatjs/intl-relativetimeformat/locale-data/es");
      break;
    default:
      // Fallback to 'en'
      await import("@formatjs/intl-relativetimeformat/locale-data/en");
  }
}

const getIntlPolyfills = (locale: string): Promise<unknown>[] => [
  polyfillPluralRules(locale),
  polyfillCanonicalLocales(),
  polyfillIntl(),
  polyfillNumberFormat(locale),
  polyfillDateTimeFormat(locale),
  polyfillRelativeTimeFormat(locale),
];

export default getIntlPolyfills;
